import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCard, Download,
  Users, HardDrive, GraduationCap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { isLyrraOne } from '@/lib/platform';
import { api } from '@/lib/api';
import LyrraOneSellerSalesPanel from '@/pages/institution/LyrraOneSellerSalesPanel';
import type { ApiResponse } from '@/types/api';

interface Invoice {
  id: string;
  number?: string;
  createdAt?: string;
  amount?: number;
  amountDue?: number;
  currency?: string;
  status: 'paid' | 'open' | 'draft' | 'void' | 'uncollectible';
  invoicePdf?: string;
  hostedInvoiceUrl?: string;
}

function UsageMeter({ label, used, max, unit, icon: Icon, color }: {
  label: string; used: number; max: number; unit: string;
  icon: React.ElementType; color: string;
}) {
  const pct = Math.min((used / max) * 100, 100);
  return (
    <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className={cn('p-2 rounded-lg bg-[#F8FAFC] dark:bg-[#19335E]/50', color)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#0C1C38] dark:text-white">{label}</p>
          <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{used} / {max} {unit}</p>
        </div>
      </div>
      <div className="w-full bg-[#E8EDF5] dark:bg-[#19335E] rounded-full h-2">
        <div
          className={cn(
            'h-2 rounded-full transition-all',
            pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-yellow-500' : 'bg-green-500'
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function InstitutionBillingLegacyPanel() {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').replace('_', '-');
  const invoiceStatusBadges = useMemo(
    () =>
      ({
        paid: { label: t('institution.billing.invPaid'), color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
        open: { label: t('institution.billing.invOpen'), color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
        draft: { label: t('institution.billing.invDraft'), color: 'bg-[#F0F4FA] text-[#0C1C38] dark:bg-[#19335E] dark:text-[#DCE7F7]' },
        void: { label: t('institution.billing.invVoid'), color: 'bg-[#F0F4FA] text-[#0C1C38] dark:bg-[#19335E] dark:text-[#DCE7F7]' },
        uncollectible: { label: t('institution.billing.invUncollectible'), color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
      }) as Record<string, { label: string; color: string }>,
    [t, i18n.language]
  );
  const [invoices, setInvoices] = React.useState<Invoice[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const envelope = await api.get<ApiResponse<{ invoices?: Invoice[] }>>('/institution/invoices');
        if (!mounted) return;
        const payload = envelope.data;
        setInvoices(payload?.invoices ?? []);
      } catch {
        if (mounted) setInvoices([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{t('institution.billing.title')}</h1>
        <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">{t('institution.billing.subtitle')}</p>
      </div>

      {/* Usage meters */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <UsageMeter label={t('institution.billing.usageCollaborators')} used={6} max={10} unit="" icon={GraduationCap} color="text-blue-600 dark:text-blue-400" />
        <UsageMeter label={t('institution.billing.usageLearners')} used={342} max={500} unit="" icon={Users} color="text-green-600 dark:text-green-400" />
        <UsageMeter label={t('institution.billing.usageStorage')} used={28} max={50} unit={t('institution.billing.storageUnit')} icon={HardDrive} color="text-purple-600 dark:text-purple-400" />
      </div>

      {/* Payment method */}
      <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-5 flex items-center gap-4">
        <div className="p-2 rounded-lg bg-[#F8FAFC] dark:bg-[#19335E]/50 text-[#5B6472] dark:text-[#B6CAEC]">
          <CreditCard className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#0C1C38] dark:text-white">{t('institution.billing.cardMasked')}</p>
          <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.billing.cardExpires', { date: '12/2026' })}</p>
        </div>
        <button className="text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium">{t('institution.common.edit')}</button>
      </div>

      {/* Invoices */}
      <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E8EDF5] dark:border-[#19335E]">
          <h3 className="font-semibold text-[#0C1C38] dark:text-white text-sm">{t('institution.billing.invoicesTitle')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8EDF5] dark:border-[#19335E] bg-[#F8FAFC] dark:bg-[#122644]/50">
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.billing.colRef')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.billing.colDate')}</th>
                <th className="text-end px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.billing.colAmount')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.billing.colStatus')}</th>
                <th className="text-end px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.billing.colPdf')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F4FA] dark:divide-[#19335E]">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#5B6472]/70 dark:text-[#B6CAEC]">{t('institution.common.loading')}</td></tr>
              ) : invoices.map((inv) => {
                const badge = invoiceStatusBadges[inv.status] ?? invoiceStatusBadges.open;
                return (
                  <tr key={inv.id} className="hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-[#0C1C38] dark:text-white">{inv.number || inv.id}</td>
                    <td className="px-4 py-3 text-[#5B6472] dark:text-[#B6CAEC]">{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString(dateLocale) : t('institution.common.empty')}</td>
                    <td className="px-4 py-3 text-end font-medium text-[#0C1C38] dark:text-white">
                      {((inv.amountDue ?? inv.amount ?? 0) / 100).toLocaleString(dateLocale, { style: 'currency', currency: (inv.currency || 'EUR').toUpperCase() })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', badge.color)}>{badge.label}</span>
                    </td>
                    <td className="px-4 py-3 text-end">
                      {inv.invoicePdf && (
                        <button
                          className="p-1 rounded hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] text-[#5B6472]/70 hover:text-[#5B6472] dark:hover:text-white"
                          onClick={() => window.open(inv.invoicePdf, '_blank')}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function InstitutionBillingPanel() {
  if (isLyrraOne()) {
    return <LyrraOneSellerSalesPanel />;
  }
  return <InstitutionBillingLegacyPanel />;
}
